import { fixtureIdsToRemove, isUpcomingFixture, isWithinUpcomingSheetWindow, normalisePlayerName, playerIsSelected, recordAppearance } from "./logic";
import { clearDatabase, commitChanges, loadSnapshot, openDatabase, removePupil, type Snapshot } from "./storage";
import {
  CURRENT_SEASON,
  type ApiErrorCode,
  type Association,
  type Fixture,
  type Pupil,
  type RefreshRun,
  type Selection,
  type SelectionStatus,
  type StoredFixture,
  type StoredTeam,
  type TeamSheetResult,
} from "../shared/contracts";

const PASSWORD_KEY = "fettes-fixtures:team-sheet-password";
const SEASON_KEY = "fettes-fixtures:season";
const LAST_NETWORK_REFRESH_KEY = "fettes-fixtures:last-network-refresh";
const LAST_FULL_SCAN_DATE_KEY = "fettes-fixtures:last-full-scan-date-v2";
const REFRESH_COOLDOWN_MS = 15 * 60 * 1000;
const SHEET_WINDOW_DAYS = 2;
declare const __APP_BUILD_ID__: string;
const STATUS_LABELS: Record<SelectionStatus, string> = {
  confirmed: "Confirmed",
  awaiting_team_sheet: "Awaiting team sheet",
  not_selected: "Not selected",
  possible: "Possible",
  unable_to_check: "Unable to check",
};

const rootElement = document.querySelector<HTMLElement>("#app");
if (!rootElement) throw new Error("App root is missing");
const root = rootElement;

let database: IDBDatabase;
let busyAction: "refresh" | "scan" | null = null;
let sportsCache: Array<{ sid: number; name: string }> | null = null;

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] ?? character);
}

function formatDate(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(date);
}

function currentScanDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${localStorage.getItem(SEASON_KEY) ?? CURRENT_SEASON}:${year}-${month}-${day}`;
}

function readErrorCode(error: unknown): ApiErrorCode {
  if (error instanceof Response) return "upstream_unavailable";
  if (error && typeof error === "object" && "code" in error && typeof error.code === "string") return error.code as ApiErrorCode;
  return "upstream_unavailable";
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  const value = await response.json() as T | { error?: { code?: string; message?: string } };
  if (!response.ok) {
    const details = "error" in (value as object) ? (value as { error?: { code?: string; message?: string } }).error : undefined;
    throw Object.assign(new Error(details?.message ?? "Request failed"), { code: details?.code ?? "upstream_unavailable" });
  }
  return value as T;
}

function fixtureStatus(snapshot: Snapshot, association: Association, fixture: StoredFixture): SelectionStatus {
  const selection = snapshot.selections.find((item) => item.fixtureId === fixture.id && item.pupilId === association.pupilId);
  if (selection) return selection.selected ? "confirmed" : "not_selected";
  if (fixture.checkError) return "unable_to_check";
  return association.confidence === "known" ? "awaiting_team_sheet" : "possible";
}

function renderFixture(snapshot: Snapshot, association: Association, team: StoredTeam, fixture: StoredFixture): string {
  const status = fixtureStatus(snapshot, association, fixture);
  const pupil = snapshot.pupils.find((item) => item.id === association.pupilId);
  const opponent = fixture.opponent || "Opponent to be confirmed";
  const matchUrl = `https://www.fettessport.com/iframeFixtureDetails.asp?ID=113&FID=${encodeURIComponent(fixture.fid)}&SID=${encodeURIComponent(team.sid)}`;
  const details = [
    `<span>Start: ${escapeHtml(fixture.time ?? "TBC")}</span>`,
    `<span>Meet: ${escapeHtml(fixture.meetTime ?? "TBC")}</span>`,
    `<span>Venue: ${escapeHtml(fixture.venue ?? "TBC")}</span>`,
  ];
  if (fixture.fixtureState !== "scheduled") details.push(`<span>${escapeHtml(fixture.fixtureState)}</span>`);
  if (fixture.transportNotes) details.push(`<span>Transport: ${escapeHtml(fixture.transportNotes)}</span>`);
  const teamSheet = fixture.teamSheet?.length
    ? `<details class="team-sheet"><summary>Full team sheet (${fixture.teamSheet.length})</summary><ol>${fixture.teamSheet.map((entry) => {
        const player = typeof entry === "string" ? { name: entry, annotations: [] } : entry;
        const isPupil = pupil && normalisePlayerName(player.name) === normalisePlayerName(pupil.displayName);
        const name = isPupil ? `<strong>${escapeHtml(player.name)}</strong>` : escapeHtml(player.name);
        const annotations = player.annotations.length
          ? ` <span class="player-annotations">${player.annotations.map(escapeHtml).join(" ")}</span>`
          : "";
        return `<li${isPupil ? " class=\"tracked-player\"" : ""}>${name}${annotations}</li>`;
      }).join("")}</ol></details>`
    : "";
  return `<li class="fixture-card">
    <div class="fixture-topline"><h4 class="fixture-heading"><a href="${matchUrl}" target="_blank" rel="external noopener">${escapeHtml(team.sport)} · ${escapeHtml(team.name)} vs ${escapeHtml(opponent)} <span aria-hidden="true">↗</span></a></h4><strong class="status status-${status}">${STATUS_LABELS[status]}</strong></div>
    <div class="fixture-details">${details.join("")}</div>
    ${teamSheet}
    ${fixture.checkError ? `<p class="check-error">The latest check failed (${escapeHtml(fixture.checkError.replaceAll("_", " "))}). The last known selection, if any, has been kept.</p>` : ""}
  </li>`;
}

function renderPupil(snapshot: Snapshot, pupilId: string): string {
  const pupil = snapshot.pupils.find((item) => item.id === pupilId);
  if (!pupil) return "";
  const pupilAssociations = snapshot.associations.filter((item) => item.pupilId === pupilId);
  const trackedTeams = pupilAssociations
    .map((association) => snapshot.teams.find((item) => item.id === association.teamId))
    .filter((team): team is StoredTeam => Boolean(team));
  const matches: Array<{ association: Association; team: StoredTeam; fixture: StoredFixture }> = [];
  for (const association of pupilAssociations) {
    const team = snapshot.teams.find((item) => item.id === association.teamId);
    if (!team) continue;
    const fixtures = snapshot.fixtures
      .filter((item) => item.teamId === team.id && isUpcomingFixture(item.date))
      .sort((left, right) => `${left.date}${left.time ?? ""}`.localeCompare(`${right.date}${right.time ?? ""}`));
    for (const fixture of fixtures) {
      matches.push({ association, team, fixture });
    }
  }
  matches.sort((left, right) => `${left.fixture.date}${left.fixture.time ?? ""}`.localeCompare(`${right.fixture.date}${right.fixture.time ?? ""}`));
  const renderMatchGroups = (items: typeof matches): string => {
    const groups = new Map<string, string[]>();
    for (const { association, team, fixture } of items) {
      const group = groups.get(fixture.date) ?? [];
      group.push(renderFixture(snapshot, association, team, fixture));
      groups.set(fixture.date, group);
    }
    return [...groups].map(([date, fixtures]) => `<section class="date-group"><h3>${escapeHtml(formatDate(date))}</h3><ul class="fixture-list">${fixtures.join("")}</ul></section>`).join("");
  };
  const visibleMatches = matches.slice(0, 2);
  const additionalMatches = matches.slice(2);
  const additionalId = `more-fixtures-${pupil.id}`;
  const fixtureMarkup = matches.length
    ? `${renderMatchGroups(visibleMatches)}${additionalMatches.length ? `<button type="button" class="secondary show-more" data-show-more="${escapeHtml(additionalId)}" data-count="${additionalMatches.length}" aria-expanded="false" aria-controls="${escapeHtml(additionalId)}">Show ${additionalMatches.length} more ${additionalMatches.length === 1 ? "match" : "matches"}</button><div id="${escapeHtml(additionalId)}" class="more-fixtures" hidden>${renderMatchGroups(additionalMatches)}</div>` : ""}`
    : `<p class="empty">No fixtures saved yet. Add a team below or scan all teams to discover a published selection.</p>`;
  const teamSummary = trackedTeams.length
    ? `<p class="tracked-teams"><strong>Tracked teams:</strong> ${trackedTeams.map((team) => `${escapeHtml(team.sport)} · ${escapeHtml(team.name)}`).join(", ")}</p>`
    : `<p class="tracked-teams"><strong>Tracked teams:</strong> None yet</p>`;
  return `<section class="pupil-section" aria-labelledby="heading-${pupilId}">
    <div class="pupil-heading"><h2 id="heading-${pupilId}">${escapeHtml(pupil.displayName)}</h2><button type="button" class="danger compact" data-remove-pupil="${escapeHtml(pupil.id)}">Remove pupil</button></div>
    ${teamSummary}${fixtureMarkup}</section>`;
}

async function render(message = ""): Promise<void> {
  const snapshot = await loadSnapshot(database);
  const lastRun = [...snapshot.refreshRuns].sort((a, b) => b.completedAt.localeCompare(a.completedAt))[0];
  const hasPassword = Boolean(localStorage.getItem(PASSWORD_KEY));
  const pupils = [...snapshot.pupils].sort((left, right) => left.displayName.localeCompare(right.displayName, "en-GB"));
  const pupilMarkup = pupils.length
    ? pupils.map((pupil) => renderPupil(snapshot, pupil.id)).join("")
    : `<section class="pupil-section"><h2>No pupils yet</h2><p class="empty">Add a pupil below before choosing or scanning teams.</p></section>`;
  const pupilOptions = pupils.map((pupil) => `<option value="${escapeHtml(pupil.id)}">${escapeHtml(pupil.displayName)}</option>`).join("");
  root.innerHTML = `<header class="page-header">
      <p class="eyebrow">Local fixture tracker</p><h1>FBSports</h1>
      <div class="refresh-row"><button id="refresh" type="button" ${busyAction ? "disabled" : ""}>${busyAction === "refresh" ? "Refreshing tracked teams…" : "Refresh tracked teams"}</button>
      <button id="scan-all-teams" type="button" class="secondary" ${busyAction || pupils.length === 0 ? "disabled" : ""}>${busyAction === "scan" ? "Scanning all teams…" : "Scan all teams"}</button>
      <p>Last refreshed: ${lastRun ? `<time datetime="${lastRun.completedAt}">${escapeHtml(new Date(lastRun.completedAt).toLocaleString("en-GB"))}</time>` : "Never"}</p></div>
      <p id="live-status" class="refresh-message" role="status" aria-live="polite">${escapeHtml(message)}</p>
    </header>
    <main>${pupilMarkup}
      <section class="settings" aria-labelledby="pupils-heading"><h2 id="pupils-heading">Pupils</h2>
        <p>Add each pupil using the exact first name and surname expected on published team sheets.</p>
        <form id="pupil-form"><label for="pupil-name">Pupil name</label><input id="pupil-name" name="pupil-name" type="text" autocomplete="off" required placeholder="Firstname Lastname">
          <button type="submit">Add pupil</button></form>
      </section>
      <section class="settings" aria-labelledby="discovery-heading"><h2 id="discovery-heading">Team selection</h2>
        <p>The app scans all teams once each day and whenever a pupil is added, checking fixtures from today through the next two days for exact name matches. Use Scan all teams to run it again manually. Refresh tracked teams updates only teams already listed above and keeps its 15-minute network cooldown.</p>
        <form id="discovery-form"><label for="pupil">Pupil</label><select id="pupil" name="pupil" required ${pupils.length ? "" : "disabled"}><option value="">Choose a pupil</option>${pupilOptions}</select>
          <label for="sport">Sport</label><select id="sport" name="sport" required><option value="">Load sports…</option></select>
          <label for="team">Team</label><select id="team" name="team" required disabled><option value="">Choose a sport first</option></select>
          <button type="submit" ${pupils.length ? "" : "disabled"}>Add team</button></form>
      </section>
      <section class="settings" aria-labelledby="privacy-heading"><h2 id="privacy-heading">Team-sheet password</h2>
        <p>Enter the password provided for viewing team sheets.</p>
        <form id="password-form"><label for="password">Team-sheet password</label><input id="password" name="password" type="password" autocomplete="off" ${hasPassword ? "placeholder=\"A password is saved\"" : ""}>
          <div class="button-row"><button type="submit">Save password</button><button id="forget-password" type="button" class="secondary" ${hasPassword ? "" : "disabled"}>Forget saved password</button></div></form>
      </section>
      <section class="settings" aria-labelledby="local-data-heading"><h2 id="local-data-heading">Local data</h2>
        <p>Clear all saved pupils, teams, fixtures, selections and refresh history from this browser. The saved team-sheet password is kept.</p>
        <button id="clear-database" type="button" class="danger" ${busyAction ? "disabled" : ""}>Clear fixture database</button>
      </section>
    </main><footer><p>All pupil, fixture and selection history stays in this browser.</p></footer>`;
  bindEvents();
  await loadSports();
}

async function checkTeamSheet(fixture: Fixture, team: StoredTeam): Promise<TeamSheetResult> {
  let result = await api<TeamSheetResult>(`/api/team-sheet?sid=${team.sid}&fid=${fixture.fid}`);
  const password = localStorage.getItem(PASSWORD_KEY);
  if (result.status === "password_required" && password) {
    result = await api<TeamSheetResult>("/api/team-sheet", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sid: team.sid, fid: fixture.fid, password }),
    });
  }
  return result;
}

function checkTeamSheetOnce(fixture: Fixture, team: StoredTeam, checks: Map<string, Promise<TeamSheetResult>>): Promise<TeamSheetResult> {
  const key = `${team.id}:${fixture.fid}`;
  const existing = checks.get(key);
  if (existing) return existing;
  const pending = checkTeamSheet(fixture, team);
  checks.set(key, pending);
  return pending;
}

async function mapWithConcurrency<T, R>(items: T[], limit: number, task: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await task(items[index]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
  return results;
}

type DiscoveryResult = {
  teams: StoredTeam[];
  associations: Association[];
  fixtures: StoredFixture[];
  selections: Selection[];
  failures: number;
  successes: number;
  authNeeded: boolean;
  firstError: ApiErrorCode | null;
  remoteFixtureIdsByTeam: Map<string, Set<string>>;
};

async function discoverPublishedSelections(snapshot: Snapshot, sheetChecks: Map<string, Promise<TeamSheetResult>>): Promise<DiscoveryResult> {
  const result: DiscoveryResult = {
    teams: [], associations: [], fixtures: [], selections: [], failures: 0, successes: 0,
    authNeeded: false, firstError: null, remoteFixtureIdsByTeam: new Map(),
  };
  const season = localStorage.getItem(SEASON_KEY) ?? CURRENT_SEASON;
  const sports = await getSports();
  const teamGroups = await mapWithConcurrency(sports, 4, async (sport) => {
    try {
      const response = await api<{ teams: Array<{ sid: number; tid: number; name: string }> }>(`/api/teams?season=${season}&sid=${sport.sid}`);
      result.successes += 1;
      return response.teams.map((team): StoredTeam => ({ ...team, id: `${season}:${team.sid}:${team.tid}`, sport: sport.name, season }));
    } catch (error) {
      result.failures += 1;
      result.firstError ??= readErrorCode(error);
      return [];
    }
  });
  const allTeams = teamGroups.flat();
  const fixtureGroups = await mapWithConcurrency(allTeams, 5, async (team) => {
    try {
      const response = await api<{ fixtures: Fixture[] }>(`/api/fixtures?sid=${team.sid}&tid=${team.tid}`);
      result.successes += 1;
      result.remoteFixtureIdsByTeam.set(team.id, new Set(response.fixtures.map((fixture) => `${team.id}:${fixture.fid}`)));
      return response.fixtures.map((fixture) => ({ team, fixture }));
    } catch (error) {
      result.failures += 1;
      result.firstError ??= readErrorCode(error);
      return [];
    }
  });

  const nearbyFixtures = fixtureGroups.flat().filter(({ fixture }) => isWithinUpcomingSheetWindow(fixture.date, new Date(), SHEET_WINDOW_DAYS));
  await mapWithConcurrency(nearbyFixtures, 6, async ({ team, fixture }) => {
    try {
      const sheet = await checkTeamSheetOnce(fixture, team, sheetChecks);
      result.successes += 1;
      if (sheet.status === "password_required" || sheet.status === "incorrect_password") {
        result.authNeeded = true;
        return;
      }
      if (sheet.status !== "published") return;
      const checkedAt = new Date().toISOString();
      const fixtureId = `${team.id}:${fixture.fid}`;
      const matchingPupils = snapshot.pupils.filter((pupil) => playerIsSelected(sheet.players, pupil.displayName));
      const teamIsTracked = snapshot.associations.some((association) => association.teamId === team.id);
      if (teamIsTracked || matchingPupils.length > 0) {
        result.teams.push(team);
        result.fixtures.push({ ...fixture, id: fixtureId, teamId: team.id, checkedAt, checkError: null, teamSheet: sheet.players });
      }
      for (const pupil of matchingPupils) {
        const associationId = `${pupil.id}:${team.id}`;
        const selectionId = `${fixtureId}:${pupil.id}`;
        const existingAssociation = snapshot.associations.find((item) => item.id === associationId);
        const existingSelection = snapshot.selections.find((item) => item.id === selectionId);
        result.selections.push({ id: selectionId, fixtureId, pupilId: pupil.id, selected: true, checkedAt });
        result.associations.push(existingSelection?.selected && existingAssociation
          ? existingAssociation
          : recordAppearance(existingAssociation ?? {
              id: associationId, pupilId: pupil.id, teamId: team.id, confidence: "candidate",
              firstSeenAt: checkedAt, lastConfirmedAt: null, appearanceCount: 0,
            }, checkedAt));
      }
    } catch (error) {
      result.failures += 1;
      result.firstError ??= readErrorCode(error);
    }
  });
  return result;
}

async function refreshFixtures(): Promise<void> {
  const now = Date.now();
  const lastNetworkRefresh = Number(localStorage.getItem(LAST_NETWORK_REFRESH_KEY) ?? "0");
  if (now - lastNetworkRefresh < REFRESH_COOLDOWN_MS) {
    const nextRefresh = new Date(lastNetworkRefresh + REFRESH_COOLDOWN_MS).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    await render(`Recently checked. To limit requests to SOCS, the next network refresh is available at ${nextRefresh}.`);
    return;
  }

  localStorage.setItem(LAST_NETWORK_REFRESH_KEY, String(now));
  busyAction = "refresh";
  await render();
  const startedAt = new Date().toISOString();
  const snapshot = await loadSnapshot(database);
  const teams = new Map(snapshot.teams.map((item) => [item.id, item]));
  const fixtures = new Map(snapshot.fixtures.map((item) => [item.id, item]));
  const selections = new Map(snapshot.selections.map((item) => [item.id, item]));
  const associations = new Map(snapshot.associations.map((item) => [item.id, item]));
  let failures = 0;
  let successes = 0;
  let firstError: ApiErrorCode | null = null;
  const sheetChecks = new Map<string, Promise<TeamSheetResult>>();
  const remoteFixtureIdsByTeam = new Map<string, Set<string>>();

  for (const originalAssociation of associations.values()) {
    const team = teams.get(originalAssociation.teamId);
    const pupil = snapshot.pupils.find((item) => item.id === originalAssociation.pupilId);
    if (!team || !pupil) continue;
    let remoteFixtures: Fixture[];
    try {
      remoteFixtures = (await api<{ fixtures: Fixture[] }>(`/api/fixtures?sid=${team.sid}&tid=${team.tid}`)).fixtures;
      remoteFixtureIdsByTeam.set(team.id, new Set(remoteFixtures.map((fixture) => `${team.id}:${fixture.fid}`)));
      successes += 1;
    } catch (error) {
      failures += 1;
      firstError ??= readErrorCode(error);
      continue;
    }
    for (const fixture of remoteFixtures) {
      const id = `${team.id}:${fixture.fid}`;
      const checkedAt = new Date().toISOString();
      const previousFixture = fixtures.get(id);
      let stored: StoredFixture = { ...fixture, id, teamId: team.id, checkedAt, checkError: null, teamSheet: previousFixture?.teamSheet ?? null };
      if (!isWithinUpcomingSheetWindow(fixture.date, new Date(), SHEET_WINDOW_DAYS)) {
        fixtures.set(id, stored);
        continue;
      }
      try {
        const sheet = await checkTeamSheetOnce(fixture, team, sheetChecks);
        if (sheet.status === "published") {
          stored = { ...stored, teamSheet: sheet.players };
          const selected = playerIsSelected(sheet.players, pupil.displayName);
          const selectionId = `${id}:${pupil.id}`;
          const previous = selections.get(selectionId);
          selections.set(selectionId, { id: selectionId, fixtureId: id, pupilId: pupil.id, selected, checkedAt });
          if (selected && !previous?.selected) {
            associations.set(originalAssociation.id, recordAppearance(associations.get(originalAssociation.id) ?? originalAssociation, checkedAt));
          }
        } else if (sheet.status === "incorrect_password" || sheet.status === "password_required") {
          stored = { ...stored, checkError: "incorrect_password" };
          failures += 1;
          firstError ??= "incorrect_password";
        }
        successes += 1;
      } catch (error) {
        stored = { ...stored, checkError: readErrorCode(error) };
        failures += 1;
        firstError ??= stored.checkError;
      }
      fixtures.set(id, stored);
    }
  }

  const removedFixtureIds = new Set(fixtureIdsToRemove(snapshot.fixtures, remoteFixtureIdsByTeam));
  for (const fixtureId of removedFixtureIds) fixtures.delete(fixtureId);
  const removedSelectionIds = snapshot.selections
    .filter((selection) => removedFixtureIds.has(selection.fixtureId))
    .map((selection) => selection.id);
  for (const selectionId of removedSelectionIds) selections.delete(selectionId);

  const completedAt = new Date().toISOString();
  const run: RefreshRun = {
    id: completedAt,
    startedAt,
    completedAt,
    outcome: failures === 0 ? "success" : successes === 0 ? "failed" : "partial",
    errorCategory: firstError,
  };
  await commitChanges(database, {
    fixtures: [...fixtures.values()],
    selections: [...selections.values()],
    associations: [...associations.values()],
    refreshRuns: [run],
  }, {
    fixtures: [...removedFixtureIds],
    selections: removedSelectionIds,
  });
  busyAction = null;
  await render(failures === 0 ? "Tracked-team refresh complete." : `Tracked-team refresh complete with ${failures} check ${failures === 1 ? "problem" : "problems"}.`);
}

async function scanAllTeams(startMessage = "Scanning all teams for fixtures in the next two days…"): Promise<void> {
  busyAction = "scan";
  await render(startMessage);
  const startedAt = new Date().toISOString();
  const snapshot = await loadSnapshot(database);
  let failures = 0;
  let successes = 0;
  let firstError: ApiErrorCode | null = null;
  let discovery: DiscoveryResult | null = null;
  try {
    discovery = await discoverPublishedSelections(snapshot, new Map());
    failures = discovery.failures + (discovery.authNeeded ? 1 : 0);
    successes = discovery.successes;
    firstError = discovery.firstError ?? (discovery.authNeeded ? "incorrect_password" : null);
    const completedAt = new Date().toISOString();
    const run: RefreshRun = {
      id: completedAt,
      startedAt,
      completedAt,
      outcome: failures === 0 ? "success" : successes === 0 ? "failed" : "partial",
      errorCategory: firstError,
    };
    const removedFixtureIds = fixtureIdsToRemove(snapshot.fixtures, discovery.remoteFixtureIdsByTeam);
    const removedFixtureIdSet = new Set(removedFixtureIds);
    const removedSelectionIds = snapshot.selections
      .filter((selection) => removedFixtureIdSet.has(selection.fixtureId))
      .map((selection) => selection.id);
    await commitChanges(database, {
      teams: discovery.teams,
      fixtures: discovery.fixtures,
      selections: discovery.selections,
      associations: discovery.associations,
      refreshRuns: [run],
    }, {
      fixtures: removedFixtureIds,
      selections: removedSelectionIds,
    });
  } catch (error) {
    failures += 1;
    firstError ??= readErrorCode(error);
  }
  busyAction = null;
  localStorage.setItem(LAST_FULL_SCAN_DATE_KEY, currentScanDateKey());
  const matches = new Set(discovery?.associations.map((item) => item.id) ?? []).size;
  await render(failures === 0
    ? `All-team scan complete. ${matches} matching ${matches === 1 ? "team" : "teams"} found in the next two days.`
    : `All-team scan complete with ${failures} check ${failures === 1 ? "problem" : "problems"}.`);
}

async function getSports(): Promise<Array<{ sid: number; name: string }>> {
  if (sportsCache) return sportsCache;
  const season = localStorage.getItem(SEASON_KEY) ?? CURRENT_SEASON;
  sportsCache = (await api<{ sports: Array<{ sid: number; name: string }> }>(`/api/sports?season=${season}`)).sports;
  return sportsCache;
}

async function loadSports(): Promise<void> {
  const select = document.querySelector<HTMLSelectElement>("#sport");
  if (!select) return;
  try {
    const sports = await getSports();
    select.innerHTML = `<option value="">Choose a sport</option>${sports.map((sport) => `<option value="${sport.sid}" data-name="${escapeHtml(sport.name)}">${escapeHtml(sport.name)}</option>`).join("")}`;
  } catch {
    select.innerHTML = `<option value="">Sports could not be loaded</option>`;
  }
}

async function loadTeams(sid: number): Promise<void> {
  const select = document.querySelector<HTMLSelectElement>("#team");
  if (!select) return;
  select.disabled = true;
  select.innerHTML = `<option value="">Loading teams…</option>`;
  try {
    const season = localStorage.getItem(SEASON_KEY) ?? CURRENT_SEASON;
    const result = await api<{ teams: Array<{ tid: number; name: string }> }>(`/api/teams?season=${season}&sid=${sid}`);
    select.innerHTML = `<option value="">Choose a team</option>${result.teams.map((team) => `<option value="${team.tid}" data-name="${escapeHtml(team.name)}">${escapeHtml(team.name)}</option>`).join("")}`;
    select.disabled = false;
  } catch {
    select.innerHTML = `<option value="">Teams could not be loaded</option>`;
  }
}

function bindEvents(): void {
  document.querySelector("#refresh")?.addEventListener("click", () => { void refreshFixtures(); });
  document.querySelector("#scan-all-teams")?.addEventListener("click", () => { void scanAllTeams(); });
  document.querySelector<HTMLSelectElement>("#sport")?.addEventListener("change", (event) => {
    const sid = Number((event.currentTarget as HTMLSelectElement).value);
    if (sid) void loadTeams(sid);
  });
  document.querySelector<HTMLFormElement>("#password-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = document.querySelector<HTMLInputElement>("#password");
    if (input?.value) {
      localStorage.setItem(PASSWORD_KEY, input.value);
      localStorage.removeItem(LAST_NETWORK_REFRESH_KEY);
    }
    void render("Password saved in this browser.");
  });
  document.querySelector("#forget-password")?.addEventListener("click", () => {
    localStorage.removeItem(PASSWORD_KEY);
    void render("Saved password forgotten.");
  });
  document.querySelector("#clear-database")?.addEventListener("click", () => { void clearLocalDatabase(); });
  document.querySelector<HTMLFormElement>("#pupil-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void addPupil();
  });
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-remove-pupil]")) {
    button.addEventListener("click", () => { void removeSelectedPupil(button.dataset.removePupil ?? ""); });
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-show-more]")) {
    button.addEventListener("click", () => {
      const target = document.getElementById(button.dataset.showMore ?? "");
      if (!target) return;
      const expanded = button.getAttribute("aria-expanded") === "true";
      target.hidden = expanded;
      button.setAttribute("aria-expanded", String(!expanded));
      const count = Number(button.dataset.count ?? "0");
      button.textContent = expanded ? `Show ${count} more ${count === 1 ? "match" : "matches"}` : "Show fewer matches";
    });
  }
  document.querySelector<HTMLFormElement>("#discovery-form")?.addEventListener("submit", (event) => {
    event.preventDefault();
    void addSelectedTeam();
  });
}

async function clearLocalDatabase(): Promise<void> {
  const confirmed = window.confirm("Clear all saved pupils, teams, fixtures, selections and refresh history? The saved team-sheet password will be kept.");
  if (!confirmed) return;
  await clearDatabase(database);
  localStorage.removeItem(LAST_NETWORK_REFRESH_KEY);
  localStorage.removeItem(LAST_FULL_SCAN_DATE_KEY);
  await render("Fixture database cleared. Add a pupil to begin.");
}

async function addPupil(): Promise<void> {
  const input = document.querySelector<HTMLInputElement>("#pupil-name");
  const displayName = input?.value.trim().replace(/\s+/g, " ") ?? "";
  if (!displayName) return;
  const snapshot = await loadSnapshot(database);
  if (snapshot.pupils.some((pupil) => normalisePlayerName(pupil.displayName) === normalisePlayerName(displayName))) {
    await render(`${displayName} is already in the pupil list.`);
    return;
  }
  const pupil: Pupil = { id: `pupil-${crypto.randomUUID()}`, displayName };
  await commitChanges(database, { pupils: [pupil] });
  await scanAllTeams(`${displayName} added. Scanning all teams now…`);
}

async function removeSelectedPupil(pupilId: string): Promise<void> {
  const snapshot = await loadSnapshot(database);
  const pupil = snapshot.pupils.find((item) => item.id === pupilId);
  if (!pupil) return;
  const confirmed = window.confirm(`Remove ${pupil.displayName} and all of their saved teams and selections?`);
  if (!confirmed) return;
  await removePupil(database, pupil.id);
  await render(`${pupil.displayName} removed.`);
}

async function addSelectedTeam(): Promise<void> {
  const pupilSelect = document.querySelector<HTMLSelectElement>("#pupil");
  const sportSelect = document.querySelector<HTMLSelectElement>("#sport");
  const teamSelect = document.querySelector<HTMLSelectElement>("#team");
  if (!pupilSelect?.value || !sportSelect?.value || !teamSelect?.value) return;
  const season = localStorage.getItem(SEASON_KEY) ?? CURRENT_SEASON;
  const sid = Number(sportSelect.value);
  const tid = Number(teamSelect.value);
  const sport = sportSelect.selectedOptions[0]?.dataset.name ?? sportSelect.selectedOptions[0]?.textContent ?? "Sport";
  const name = teamSelect.selectedOptions[0]?.dataset.name ?? teamSelect.selectedOptions[0]?.textContent ?? "Team";
  const team: StoredTeam = { id: `${season}:${sid}:${tid}`, sid, tid, sport, name, season };
  const now = new Date().toISOString();
  const association: Association = { id: `${pupilSelect.value}:${team.id}`, pupilId: pupilSelect.value, teamId: team.id, confidence: "candidate", firstSeenAt: now, lastConfirmedAt: null, appearanceCount: 0 };
  await commitChanges(database, { teams: [team], associations: [association] });
  localStorage.removeItem(LAST_NETWORK_REFRESH_KEY);
  const pupilName = pupilSelect.selectedOptions[0]?.textContent ?? "Pupil";
  await render(`${name} added for ${pupilName}. Refresh fixtures to check it.`);
}

async function start(): Promise<void> {
  localStorage.setItem(SEASON_KEY, localStorage.getItem(SEASON_KEY) ?? CURRENT_SEASON);
  database = await openDatabase();
  await render();
  const snapshot = await loadSnapshot(database);
  if (snapshot.pupils.length > 0 && localStorage.getItem(LAST_FULL_SCAN_DATE_KEY) !== currentScanDateKey()) {
    await scanAllTeams("Running today’s automatic all-team scan…");
  } else if (snapshot.associations.length > 0) {
    await refreshFixtures();
  }
}

void start().catch((error: unknown) => {
  root.innerHTML = `<h1>FBSports</h1><p role="alert">The local fixture database could not be opened. ${escapeHtml(error instanceof Error ? error.message : "Unknown error")}</p>`;
});

async function refreshFrontendWhenUpdated(): Promise<void> {
  try {
    const response = await fetch("/version.json", { cache: "no-store" });
    if (!response.ok) return;
    const version = await response.json() as { buildId?: string };
    if (version.buildId && version.buildId !== __APP_BUILD_ID__) window.location.reload();
  } catch {
    // Keep the current build when the server cannot be reached.
  }
}

async function removeLegacyServiceWorker(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const reloadWithoutWorker = Boolean(navigator.serviceWorker.controller && registration);
    await registration?.unregister();
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys
        .filter((key) => key.startsWith("fettes-fixtures-shell-"))
        .map((key) => caches.delete(key)));
    }
    if (reloadWithoutWorker) window.location.reload();
  } catch {
    // A failed cleanup must not prevent the app from starting.
  }
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void removeLegacyServiceWorker().then(refreshFrontendWhenUpdated);
  });
}
window.addEventListener("focus", () => { void refreshFrontendWhenUpdated(); });
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void refreshFrontendWhenUpdated();
});
